import type { paths } from "@src/packages/openapi/schema";

type PhaseResponse =
  paths["/{tenantId}/cycles/{id}/phases"]["get"]["responses"][200]["content"]["application/json"];

export type ApiFertilityMetric = NonNullable<
  PhaseResponse["data"][number]["fertilityMetrics"]
>[number];

type FertilityMetricType = Exclude<ApiFertilityMetric["type"], null>;

export const STEP_FIELD_TO_METRIC: Record<string, FertilityMetricType> = {
  eggsCollected: "retrieved_oocytes",
  matureEggs: "m2_count",
  // The rest of the retrieval's own decomposition:
  // retrieved = mature (m2_count) + immature + degenerated. These two had
  // nowhere to live until dashboard-api #1734 added them to
  // `cycles_fertility_metrics_type`; before that the client could only record
  // them as a per-specimen `maturityStage`, and it writes one row per *frozen*
  // oocyte — while an immature or degenerated one is by definition not frozen,
  // so anything past the frozen count had no row to be written to and the
  // figures vanished on reopening.
  immatureMI: "immature_oocytes",
  degenerated: "degenerated_oocytes",
  eggsFrozen: "frozen_embryo",
  eggsInjected: "injected_oocytes",
  eggsFertilized: "fertilized_oocytes",
  embryosTransferred: "transferred_embryo",
  embryosFrozen: "frozen_embryo",
  embryosDay3: "embryos_day_3",
  embryosDay5: "embryos_day_5",
};

type PhaseWithMetrics = {
  /**
   * Only each existing row's identity is read here, so only that is asked for.
   * Narrower than the full API row on purpose: it keeps the type derived from
   * the schema while letting this rule be asserted without assembling nine
   * audit columns a caller of `buildFertilityMetrics` never looks at.
   */
  fertilityMetrics?: Pick<ApiFertilityMetric, "id" | "type">[];
};

/**
 * Serializes treatment counts without collapsing a clinical zero into null.
 * Missing values stay absent so the API can distinguish unknown from zero.
 *
 * A `null` value is still skipped, and that is deliberate: `createEmptyCycle`
 * defaults all four oocyte counts to null and `handleAddAnotherSample` leaves
 * the prefill unapplied, so a form nobody touched is indistinguishable from a
 * deliberate clear. Writing nulls blindly was tried and reverted in
 * `bfde19e30`, because it PATCHed `retrieved_oocytes` and `m2_count` to null on
 * cycles whose figures she had already recorded.
 *
 * Taking a count back therefore needs its own signal, and `cleared` is it: the
 * fields she explicitly marked unknown, carried separately from their values
 * precisely so "she said unknown" and "not filled in" cannot be confused. A
 * cleared field emits `value: null` against the row's own id, which is what the
 * phase PATCH accepts as an erasure. With no existing row there is nothing to
 * erase, so nothing is written — clearing a figure she never recorded must not
 * create one.
 */
/**
 * One write against a phase's metric rows.
 *
 * Mirrors the contract's own union: a row carries a value, or it carries
 * `op: "delete"` and nothing but its id. The delete variant has no `type` —
 * sending one would add a field the route does not model.
 */
export type FertilityMetricWrite =
  | { id?: string; type: FertilityMetricType; value: number | null }
  | { id: string; op: "delete" };

export function buildFertilityMetrics(
  data: Record<string, string | number | boolean | null>,
  existingPhase?: PhaseWithMetrics | null,
  /** Step fields she explicitly marked unknown, to erase rather than skip. */
  cleared?: readonly string[],
  /**
   * Step fields whose recorded answer she has withdrawn entirely — she had said
   * "I don't know" and has now un-said it without giving a figure. The row goes,
   * because the record should read as silence again rather than as an answer.
   */
  removed?: readonly string[],
): FertilityMetricWrite[] | undefined {
  const metrics: FertilityMetricWrite[] = [];
  const seenTypes = new Map<FertilityMetricType, number>();

  const clearedFields = new Set(cleared ?? []);
  const removedFields = new Set(removed ?? []);

  for (const [field, metricKey] of Object.entries(STEP_FIELD_TO_METRIC)) {
    const val = data[field];
    const isCleared = clearedFields.has(field);
    const isRemoved = removedFields.has(field);
    if (!isCleared && isRemoved) {
      /*
       * Withdrawing an answer is a deletion, not a null. A null row still says
       * "I don't know" — it is what `clearedOocyteCounts` reads back — so
       * leaving one behind would re-select the toggle she had just turned off,
       * with no number to type as a way out. Nothing recorded means nothing to
       * withdraw.
       */
      let priorRow: Pick<ApiFertilityMetric, "id" | "type"> | undefined;
      for (const candidate of existingPhase?.fertilityMetrics ?? []) {
        if (candidate.type === metricKey) priorRow = candidate;
      }
      if (priorRow?.id && !seenTypes.has(metricKey)) {
        // The delete variant carries the id and nothing else — the contract's
        // union has no `type` on it, and the batch routes are strict.
        seenTypes.set(metricKey, metrics.length);
        metrics.push({ id: priorRow.id, op: "delete" });
      }
      continue;
    }
    if (
      !isCleared &&
      (val === undefined ||
        val === null ||
        (typeof val === "string" && val.trim() === ""))
    ) {
      continue;
    }
    /*
     * An explicit unknown wins over whatever value came alongside it. The two
     * should never arrive together — a cleared count is null on the cycle — but
     * if they did, what she said outranks what the form happened to hold.
     */
    const numericValue = isCleared
      ? null
      : typeof val === "number"
        ? val
        : Number(val);
    if (numericValue !== null && !Number.isFinite(numericValue)) continue;

    const existingIndex = seenTypes.get(metricKey);
    const seen =
      existingIndex !== undefined ? metrics[existingIndex] : undefined;
    if (existingIndex !== undefined) {
      /*
       * Two step fields can share one metric type — `eggsFrozen` and
       * `embryosFrozen` are both `frozen_embryo` — so the later one normally
       * refines what the earlier wrote. A clear must not: it would erase a
       * figure its sibling has just supplied, silently and from a field she was
       * not even editing. Clearing is about the field she marked, not about
       * every field that happens to share its row.
       */
      // A withdrawal already claimed this row; nothing refines a deletion.
      if (!isCleared && seen && !("op" in seen)) seen.value = numericValue;
      continue;
    }

    /**
     * The LAST row of a type, because that is the one `deriveStepProgress`
     * displays — it assigns in array order, so the last write wins the screen.
     * Targeting the first (`find`) meant that if two rows of one type ever
     * coexisted — nothing forbids it, `(phase_id, type)` carries no unique
     * constraint — a correction updated one row while the screen kept
     * rendering the other, so the fix appeared to do nothing however many
     * times she retried. Written without `findLast` to stay inside the
     * engine's baseline.
     */
    let existing: Pick<ApiFertilityMetric, "id" | "type"> | undefined;
    for (const candidate of existingPhase?.fertilityMetrics ?? []) {
      if (candidate.type === metricKey) existing = candidate;
    }

    /*
     * Nothing to erase means nothing to write. An erasure is an update of a row
     * that exists — without one, emitting `value: null` would create a row
     * whose only content is the absence of a figure she never gave.
     */
    if (isCleared && !existing?.id) continue;

    seenTypes.set(metricKey, metrics.length);
    const metric: {
      id?: string;
      type: FertilityMetricType;
      value: number | null;
    } = {
      type: metricKey,
      value: numericValue,
    };
    if (existing?.id) metric.id = existing.id;
    metrics.push(metric);
  }

  return metrics.length > 0 ? metrics : undefined;
}

/** The four retrieval counts, keyed by the step field each is written to. */
const OOCYTE_COUNT_FIELDS = {
  eggsCollected: "totalRetrieved",
  matureEggs: "matureMII",
  immatureMI: "immatureMI",
  degenerated: "degenerated",
} as const;

export type ClearedOocyteCount =
  (typeof OOCYTE_COUNT_FIELDS)[keyof typeof OOCYTE_COUNT_FIELDS];

/**
 * Which counts her record marks unknown, read back from the phase.
 *
 * The record keeps the two cases apart and this is where that survives into the
 * form. `deriveStepProgress` writes `data[field] = null` for a metric row that
 * exists with no value — an answer of "I don't know" — and writes no key at all
 * for a row that was never created. So presence with a null is her statement,
 * and absence is silence.
 *
 * Without this the clear still held (the figure does not come back), but the
 * form showed it as a field she had simply not filled in, which is a different
 * thing to say back to her.
 */
export function clearedOocyteCounts(
  stepData: Partial<Record<string, unknown>>,
): ClearedOocyteCount[] {
  const cleared: ClearedOocyteCount[] = [];
  for (const [field, metric] of Object.entries(OOCYTE_COUNT_FIELDS)) {
    if (field in stepData && stepData[field] === null) cleared.push(metric);
  }
  return cleared;
}
